import { Component, OnDestroy, OnInit, ViewEncapsulation, HostBinding } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { PortRequestFormServiceService } from 'src/app/shared/service/port-request-FormService/port-request-form.service.service';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';
import { PcMultiSelectOption } from 'src/app/shared/components/pc-multi-select-filter/pc-multi-select-filter.component';
import { ConfirmPopupComponent } from 'src/app/modules/confirm-popup/confirm-popup.component';
import { formatPcUtcDateTime } from 'src/app/shared/helpers/pc-utc-datetime.util';
import html2canvas from 'html2canvas';

type OptimizationSection = 'actual' | 'expected' | 'baseline';

@Component({
  selector: 'app-pc-optimizationview',
  templateUrl: './pc-optimizationview.component.html',
  styleUrls: ['./pc-optimizationview.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PcOptimizationviewComponent implements OnInit, OnDestroy {

  private static readonly BODY_SCROLL_CLASS = 'pc-optimization-scroll';

  cargoTypes: string[] = ['Veg Oil', 'Crude Oil'];

  segments: any[] = [];
  operations: any[] = [];
  operationsCounts: Record<string, number> = {};
  ports: any[] = [];
  portsCounts: Record<string, number> = {};
  vesselNameOptions: string[] = [];
  private vesselImoByName = new Map<string, string>();
  private vesselImoFromSegmentApi = new Map<string, string>();
  private vesselCatalogLoaded = false;
  private vesselCatalogRequest: Observable<void> | null = null;

  filters: any = {
    segment: [],
    operation: [],
    port: [],
    cargoQty: '',
    expectedCargoQty: '',
    expectedCargoDays: '',
    expectedIdleTimeDays: '',
    oil: [],
  };

  // Vessel & Port Details Section State
  vesselPortVisits: any[] = [];
  selectedVesselPortVisit: any = null;
  selectedVisitIndex = 0;
  isLoadingVesselPort = false;
  optVoyageViewMode: 'list' | 'cards' = 'list';
  optVoyageSearchTerm = '';

  actualOptimizationResult: any = null;
  expectedOptimizationResult: any = null;
  technicalBaselineResult: any = null;
  private lastSubmittedExpectedIdleTimeDays: number | null = null;
  private analyzeRequestSeq = 0;
  private vesselPortRequestSeq = 0;

  /** Display string for Expected Cargo Qty filter (thousand separators). */
  expectedCargoQtyDisplay = '';
  private expectedCargoQtyFocused = false;

  /** Editable total; auto-summed from expected optimization consumption parts. */
  totalExpectedBunkerConsumption: number | null = null;
  private totalExpectedBunkerManual = false;

  isLoading = false;
  isLoadingActual = false;
  isLoadingExpected = false;
  screenshotPreview: string | null = null;
  
  // Individual Loading States to prevent flickering
  isSegmentLoading = false;
  isOperationLoading = false;
  isPortLoading = false;
  isOilLoading = false;
  isVesselLoading = false;

  // Modern Dropdown States
  segmentDropdownOpen = false;
  operationDropdownOpen = false;
  portDropdownOpen = false;
  oilDropdownOpen = false;

  segmentSearchQuery = '';
  operationSearchQuery = '';
  portSearchQuery = '';
  oilSearchQuery = '';
  selectedSegmentKeys: string[] = [];
  selectedOperationValues: string[] = [];
  selectedPortNames: string[] = [];
  selectedOilTypes: string[] = [];
  selectedVesselNames: string[] = [];
  private segmentCascadeSeq = 0;
  subusername: any;

  constructor(
    private service: PortRequestFormServiceService,
    private toastr: ToastrService,
    private requestFormService: RequestFormService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.subusername = localStorage.getItem('loginPic') ?? '';
    document.body.classList.add(PcOptimizationviewComponent.BODY_SCROLL_CLASS);
    this.selectedOperationValues = (this.selectedOperationValues || []).filter((op: any) =>
      this.isValidOperation(op),
    );
    this.filters.operation = (this.filters.operation || []).filter((op: any) =>
      this.isValidOperation(op),
    );
    this.getSegments();
    this.loadVesselImoCatalog().subscribe();
  }

  ngOnDestroy(): void {
    document.body.classList.remove(PcOptimizationviewComponent.BODY_SCROLL_CLASS);
  }

  isDarkMode(): boolean {
    return this.requestFormService.isDarkMode();
  }

  @HostBinding('class.pc-light') get pcLight(): boolean {
    return !this.isDarkMode();
  }
  private filterDemoVesselsByName(rows: any[]): any[] {
  if (this.subusername?.toLowerCase() !== 'demo') {
    return rows || [];
  }

  const DEMO_VESSEL_NAMES = ['MIGJORN', 'LLEVANT'];

  return (rows || []).filter((item: any) => {
    // Handle string items or object entries with vessel_name property
    const name = typeof item === 'string' ? item : item?.vessel_name ?? item?.name ?? '';
    return DEMO_VESSEL_NAMES.includes(String(name).trim().toUpperCase());
  });
}
private demoVesselOriginalNames = new Map<string, string>();
 loadVesselNamesForSegments(segments: string[], requestSeq?: number): void {
  const normalizedSegments = [
    ...new Set(
      (Array.isArray(segments) ? segments : [segments])
        .map((segment) => String(segment).trim())
        .filter(Boolean),
    ),
  ];

  if (!normalizedSegments.length) {
    this.vesselNameOptions = [];
    this.selectedVesselNames = [];
    return;
  }

  this.isVesselLoading = true;
  forkJoin({
    vesselNames: this.service.getVesselNames({ segment: normalizedSegments }),
    vesselCatalog: this.loadVesselImoCatalog(),
  }).subscribe({
    next: ({ vesselNames: res }) => {
      if (requestSeq != null && requestSeq !== this.segmentCascadeSeq) {
        return;
      }

      const entries = this.parseVesselNameEntries(res);
      const isDemo = this.subusername?.toLowerCase() === 'demo';

      if (isDemo) {
  const DEMO_VESSEL_NAMES = ['MIGJORN', 'LLEVANT'];

  const demoEntries = entries.filter((entry: any) => {
    const name = String(
      entry.name ?? entry.vessel_name ?? ''
    ).trim().toUpperCase();

    return DEMO_VESSEL_NAMES.includes(name);
  });

  demoEntries.forEach((entry: any, index) => {
    const originalName = entry.name;
    const maskedName = `Vessel ${index + 1}`;

    // Keep original vessel name for API
    this.demoVesselOriginalNames.set(maskedName, originalName);

    entry.originalName = originalName;
    entry.name = maskedName;
  });

  this.vesselNameOptions = demoEntries
    .map((entry) => entry.name)
    .sort((a, b) => {
      const numA = Number(a.replace('Vessel ', ''));
      const numB = Number(b.replace('Vessel ', ''));
      return numA - numB;
    });

  this.mergeVesselImoEntries(demoEntries, res);
} else {
        this.vesselNameOptions = entries
          .map((entry) => entry.name)
          .sort((a, b) => a.localeCompare(b));

        this.mergeVesselImoEntries(entries, res);
      }

      const nameLookup = new Set(
        this.vesselNameOptions.map((name) => this.normalizeVesselLookupKey(name)),
      );
      this.selectedVesselNames = this.selectedVesselNames.filter((name) =>
        nameLookup.has(this.normalizeVesselLookupKey(name)),
      );
      this.isVesselLoading = false;
    },
    error: () => {
      if (requestSeq != null && requestSeq !== this.segmentCascadeSeq) {
        return;
      }
      this.vesselNameOptions = [];
      this.isVesselLoading = false;
    },
  });
}


  get vesselDropdownPlaceholder(): string {
    if (this.isVesselLoading) {
      return 'Loading vessels...';
    }
    if (this.selectedSegmentKeys.length && !this.vesselNameOptions.length) {
      return 'No vessels available';
    }
    return 'Select Vessel';
  }

  private normalizeSegmentValues(segments: string[]): string[] {
    return [
      ...new Set(
        (Array.isArray(segments) ? segments.flat() : [segments])
          .map((segment) => String(segment).toLowerCase().trim())
          .filter(Boolean),
      ),
    ];
  }

  onVesselSelectionChange(selected: string[]): void {
    this.selectedVesselNames = selected || [];
    this.fetchVesselPortDetails();
  }

  private loadVesselImoCatalog(): Observable<void> {
    if (this.vesselCatalogLoaded) {
      return of(undefined);
    }

    if (!this.vesselCatalogRequest) {
      this.vesselCatalogRequest = this.fetchAllVesselImoSources().pipe(
        shareReplay(1),
      );
    }

    return this.vesselCatalogRequest;
  }

  private fetchAllVesselImoSources(): Observable<void> {
    return forkJoin({
      onboarding: this.service
        .getOnboardingVesselsData()
        .pipe(catchError(() => of([]))),
      catalog: this.service.getVessels().pipe(catchError(() => of({ vessels: [] }))),
      fleetActive: this.service
        .getFleetViewData('true')
        .pipe(catchError(() => of({ data: [] }))),
      fleetInactive: this.service
        .getFleetViewData('false')
        .pipe(catchError(() => of({ data: [] }))),
      q88: this.service.getQ88VesselsData().pipe(catchError(() => of([]))),
    }).pipe(
      map(({ onboarding, catalog, fleetActive, fleetInactive, q88 }) => {
        const onboardingList = Array.isArray(onboarding) ? onboarding : [];
        const catalogList = Array.isArray(catalog?.vessels)
          ? catalog.vessels
          : Array.isArray(catalog)
            ? catalog
            : [];
        const fleetActiveList = this.extractFleetVesselRows(fleetActive);
        const fleetInactiveList = this.extractFleetVesselRows(fleetInactive);
        const q88List = this.extractFleetVesselRows(q88);

        this.indexVesselCatalog([
          ...onboardingList,
          ...catalogList,
          ...fleetActiveList,
          ...fleetInactiveList,
          ...q88List,
        ]);
        this.vesselCatalogLoaded = true;
      }),
      catchError(() => of(undefined)),
    );
  }

  private extractFleetVesselRows(res: unknown): Record<string, unknown>[] {
    if (Array.isArray(res)) {
      return res as Record<string, unknown>[];
    }

    if (!res || typeof res !== 'object') {
      return [];
    }

    const record = res as Record<string, unknown>;
    for (const key of ['data', 'vessels', 'results']) {
      const rows = record[key];
      if (Array.isArray(rows)) {
        return rows as Record<string, unknown>[];
      }
    }

    return [];
  }

  private indexVesselCatalog(vessels: Record<string, unknown>[]): void {
    const nextMap = new Map(this.vesselImoByName);

    vessels.forEach((vessel) => {
      const name = this.extractVesselName(vessel);
      const imo = this.extractVesselImo(vessel);
      if (name && imo) {
        this.setVesselImoAliases(nextMap, name, imo);
      }
    });

    this.vesselImoByName = nextMap;
  }

  private setVesselImoAliases(
    map: Map<string, string>,
    name: string,
    imo: string,
  ): void {
    const normalized = this.normalizeVesselLookupKey(name);
    map.set(normalized, imo);

    const withoutPrefix = normalized.replace(/^(MT|MV|CS|ST)\s+/, '').trim();
    if (withoutPrefix && withoutPrefix !== normalized && !map.has(withoutPrefix)) {
      map.set(withoutPrefix, imo);
    }
  }

  private parseVesselNameEntries(res: any): Array<{ name: string; imo?: string }> {
    const rawEntries = Array.isArray(res?.vessel_name) ? res.vessel_name : [];
    const entries: Array<{ name: string; imo?: string }> = [];

    rawEntries.forEach((item: unknown) => {
      if (typeof item === 'string') {
        const name = item.trim();
        if (name) {
          entries.push({ name });
        }
        return;
      }

      if (!item || typeof item !== 'object') {
        return;
      }

      const record = item as Record<string, unknown>;
      const name = this.extractVesselName(record);
      const imo = this.extractVesselImo(record);
      if (name) {
        entries.push(imo ? { name, imo } : { name });
      }
    });

    const imoByName = res?.vessel_imo;
    if (Array.isArray(imoByName)) {
      entries.forEach((entry, index) => {
        if (entry.imo) {
          return;
        }
        const mappedImo = this.extractImoDigits(imoByName[index]);
        if (mappedImo) {
          entry.imo = mappedImo;
        }
      });
    } else if (
      imoByName &&
      typeof imoByName === 'object' &&
      !Array.isArray(imoByName)
    ) {
      entries.forEach((entry) => {
        if (entry.imo) {
          return;
        }
        const mappedImo = this.extractVesselImoFromMap(
          imoByName as Record<string, unknown>,
          entry.name,
        );
        if (mappedImo) {
          entry.imo = mappedImo;
        }
      });
    }

    return entries;
  }

  private mergeVesselImoEntries(
    entries: Array<{ name: string; imo?: string }>,
    res?: any,
  ): void {
    const nextMap = new Map(this.vesselImoByName);

    entries.forEach((entry) => {
      if (!entry.imo) {
        return;
      }
      this.setVesselImoAliases(nextMap, entry.name, entry.imo);
      this.vesselImoFromSegmentApi.set(
        this.normalizeVesselLookupKey(entry.name),
        entry.imo,
      );
    });

    const imoByName = res?.vessel_imo;
    if (imoByName && typeof imoByName === 'object' && !Array.isArray(imoByName)) {
      Object.entries(imoByName as Record<string, unknown>).forEach(
        ([name, imo]) => {
          const imoStr = this.extractImoDigits(imo);
          if (name && imoStr) {
            this.setVesselImoAliases(nextMap, name, imoStr);
            this.vesselImoFromSegmentApi.set(
              this.normalizeVesselLookupKey(name),
              imoStr,
            );
          }
        },
      );
    }

    this.vesselImoByName = nextMap;
  }

  private extractImoDigits(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return String(value).replace(/\D/g, '').trim();
  }

  private refreshVesselImoForAnalyze(): Observable<void> {
    const segments = this.resolveSegmentPayload();
    const requests: Observable<unknown>[] = [
      this.fetchAllVesselImoSources(),
    ];

    if (segments.length) {
      requests.push(
        this.service
          .getVesselNames({ segment: segments })
          .pipe(catchError(() => of(null))),
      );
    }

    return forkJoin(requests).pipe(
      map((results) => {
        const vesselNamesRes = segments.length ? results[1] : null;
        if (vesselNamesRes) {
          const entries = this.parseVesselNameEntries(vesselNamesRes);
          this.mergeVesselImoEntries(entries, vesselNamesRes);
        }
      }),
      catchError(() => of(undefined)),
    );
  }

  private extractVesselImoFromMap(
    imoByName: Record<string, unknown>,
    vesselName: string,
  ): string {
    const lookupKeys = [
      vesselName,
      this.normalizeVesselLookupKey(vesselName),
      vesselName.toUpperCase(),
    ];

    for (const key of lookupKeys) {
      const value = imoByName[key];
      if (value !== undefined && value !== null && value !== '') {
        return this.extractImoDigits(value);
      }
    }

    const targetKey = this.normalizeVesselLookupKey(vesselName);
    const targetCompact = this.compactVesselLookupKey(vesselName);

    for (const [key, value] of Object.entries(imoByName)) {
      const normalizedKey = this.normalizeVesselLookupKey(key);
      if (normalizedKey === targetKey) {
        return this.extractImoDigits(value);
      }

      const compactKey = normalizedKey.replace(/[^A-Z0-9]/g, '');
      if (
        targetCompact &&
        compactKey &&
        (compactKey === targetCompact ||
          compactKey.includes(targetCompact) ||
          targetCompact.includes(compactKey))
      ) {
        return this.extractImoDigits(value);
      }
    }

    return '';
  }

  private extractVesselName(vessel: Record<string, unknown>): string {
    return String(
      vessel['vessel_name'] ??
        vessel['vesselName'] ??
        vessel['ship_name'] ??
        vessel['shipName'] ??
        vessel['name'] ??
        '',
    ).trim();
  }

  private extractVesselImo(vessel: Record<string, unknown>): string {
    const raw =
      vessel['vessel_imo'] ??
      vessel['vesselIMO'] ??
      vessel['imo'] ??
      vessel['IMO'] ??
      vessel['imo_number'] ??
      vessel['imoNumber'] ??
      vessel['IMO_NUMBER'] ??
      '';

    return this.extractImoDigits(raw);
  }

  private normalizeVesselLookupKey(name: string): string {
    return String(name).trim().toUpperCase().replace(/\s+/g, ' ');
  }

  private compactVesselLookupKey(name: string): string {
    return this.normalizeVesselLookupKey(name).replace(/[^A-Z0-9]/g, '');
  }

  private lookupVesselImo(vesselName: string): string {
    const lookupKey = this.normalizeVesselLookupKey(vesselName);
    const lookupKeys = [
      lookupKey,
      lookupKey.replace(/^(MT|MV|CS|ST)\s+/, '').trim(),
    ].filter(Boolean);

    for (const key of lookupKeys) {
      const fromSegmentApi = this.vesselImoFromSegmentApi.get(key);
      if (fromSegmentApi) {
        return fromSegmentApi;
      }

      const direct = this.vesselImoByName.get(key);
      if (direct) {
        return direct;
      }
    }

    const compact = this.compactVesselLookupKey(vesselName);
    if (!compact) {
      return '';
    }

    for (const [catalogKey, imo] of this.vesselImoByName.entries()) {
      const catalogCompact = catalogKey.replace(/[^A-Z0-9]/g, '');
      if (!catalogCompact) {
        continue;
      }
      if (
        catalogCompact === compact ||
        catalogCompact.includes(compact) ||
        compact.includes(catalogCompact)
      ) {
        return imo;
      }
    }

    return '';
  }

  private resolveVesselImo(vesselName: string): string {
    const direct = this.lookupVesselImo(vesselName);
    if (direct) {
      return direct;
    }

    if (this.vesselPortVisits?.length) {
      const match = this.vesselPortVisits.find(
        (v: any) =>
          v.vessel_name &&
          (v.vessel_name.toLowerCase() === vesselName.toLowerCase() ||
            this.normalizeVesselLookupKey(v.vessel_name) ===
              this.normalizeVesselLookupKey(vesselName)),
      );
      if (match?.vessel_imo) {
        return String(match.vessel_imo);
      }
    }

    if (this.selectedVesselPortVisit?.vessel_imo) {
      return String(this.selectedVesselPortVisit.vessel_imo);
    }

    return '';
  }

  private resolveSelectedVesselImos(): string[] {
    return (this.selectedVesselNames || [])
      .map((name) => this.lookupVesselImo(name))
      .filter(Boolean);
  }

private resolveSelectedVesselNames(): string[] {
  return (this.selectedVesselNames || []).map((name) => {
    if (this.subusername?.toLowerCase() === 'demo') {
      return this.demoVesselOriginalNames.get(name) || name;
    }

    return name;
  });
}

  formatMetric(value: number | string | null | undefined, decimals = 2): string {
    if (value === null || value === undefined || value === '') {
      return '--';
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(decimals) : '--';
  }

  /**
   * Cargo quantity display: thousand separators + preserved decimal precision.
   */
  formatCargoQuantity(
    value: number | string | null | undefined,
    decimals = 2,
  ): string {
    const parsed = this.toNumber(value);
    if (parsed === null) {
      return '--';
    }
    return parsed.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  formatActualField(key: string, decimals = 2): string {
    return this.formatMetric(
      this.toNumber(this.actualOptimizationResult?.[key]),
      decimals,
    );
  }

  formatExpectedField(key: string, decimals = 2): string {
    return this.formatMetric(
      this.toNumber(this.expectedOptimizationResult?.[key]),
      decimals,
    );
  }

  formatBaselineField(key: string, decimals = 2): string {
    return this.formatMetric(
      this.toNumber(this.technicalBaselineResult?.[key]),
      decimals,
    );
  }

  formatAverageCargoQtyDisplay(): string {
    return this.formatCargoQuantity(
      this.actualOptimizationResult?.['avg_cargo_qty'],
    );
  }

  hasAverageCargoQty(): boolean {
    return this.toNumber(this.actualOptimizationResult?.['avg_cargo_qty']) !== null;
  }

  formatExpectedCargoQtyDisplay(): string {
    return this.formatCargoQuantity(
      this.expectedOptimizationResult?.['expected_cargo_qty'] ??
        this.filters.expectedCargoQty,
    );
  }

  hasExpectedCargoQty(): boolean {
    return (
      this.toNumber(this.expectedOptimizationResult?.['expected_cargo_qty']) !==
        null || this.toNumber(this.filters.expectedCargoQty) !== null
    );
  }

  formatTotalExpectedBunkerDisplay(): string {
    return this.formatMetric(this.totalExpectedBunkerConsumption, 2);
  }

  formatTechnicalBaselineTotalDisplay(): string {
    return this.formatBaselineField('total_techbasline_consumption');
  }

  hasTechnicalBaselineTotal(): boolean {
    return (
      this.toNumber(
        this.technicalBaselineResult?.['total_techbasline_consumption'],
      ) !== null
    );
  }

  formatExpectedIdleTimeDisplay(): string {
    return this.formatMetric(this.lastSubmittedExpectedIdleTimeDays, 2);
  }

  formatBaselineBunkerConsumption(): string {
    return this.formatBaselineOperationConsumption();
  }

  formatBaselineDaysRequired(): string {
    const baselineDays = this.formatBaselineField('cargo_days_required', 2);
    if (baselineDays !== '--') {
      return baselineDays;
    }

    const baselineConsumptionPerDay = this.formatBaselineField(
      'consumption_per_day',
      2,
    );
    if (baselineConsumptionPerDay !== '--') {
      return baselineConsumptionPerDay;
    }

    const expectedDays = this.formatExpectedField('cargo_days_required', 2);
    if (expectedDays !== '--') {
      return expectedDays;
    }

    return this.formatExpectedField('consumption_per_day', 2);
  }

  formatBaselineLoadDischargeRate(): string {
    const baselineValue = this.formatBaselineField('avg_loading_discharging_rate');
    if (baselineValue !== '--') {
      return baselineValue;
    }
    return this.formatExpectedField('avg_loading_discharging_rate');
  }

  formatBaselineIdleConsumptionPerDay(): string {
    return this.formatBaselineField('design_idle_consumption');
  }

  formatBaselineManoeuvringConsumption(): string {
    const baseline = this.formatBaselineField(
      this.resolveBaselineManoeuvringConsumptionKey(),
    );
    if (baseline !== '--') {
      return baseline;
    }
    return this.formatExpectedField('manoevering_consumption_per_day');
  }

  formatBaselineManoeuvringTime(): string {
    const baseline = this.formatBaselineField(
      this.resolveBaselineManoeuvringTimeKey(),
    );
    if (baseline !== '--') {
      return baseline;
    }
    return this.formatExpectedField('manoevering_time_days', 2);
  }

  private resolveBaselineManoeuvringConsumptionKey(): string {
    const candidates = [
      'manoevering_consumption_per_day',
      'manoeuvring_consumption_per_day',
      'design_manoevering_consumption',
      'design_manoeuvring_consumption',
      'manoevering_consumption',
      'manoeuvring_consumption',
    ];
    for (const key of candidates) {
      if (this.toNumber(this.technicalBaselineResult?.[key]) !== null) {
        return key;
      }
    }
    return candidates[0];
  }

  private resolveBaselineManoeuvringTimeKey(): string {
    const candidates = [
      'manoevering_time_days',
      'manoeuvring_time_days',
      'design_manoevering_time_days',
      'design_manoeuvring_time_days',
    ];
    for (const key of candidates) {
      if (this.toNumber(this.technicalBaselineResult?.[key]) !== null) {
        return key;
      }
    }
    return candidates[0];
  }

  hasTotalExpectedBunkerConsumption(): boolean {
    return this.totalExpectedBunkerConsumption != null;
  }

  onExpectedCargoQtyInput(raw: string): void {
    this.expectedCargoQtyDisplay = raw;
    const parsed = this.parseFormattedNumber(raw);
    this.filters.expectedCargoQty = parsed === null ? '' : parsed;
  }

  onExpectedCargoQtyFocus(): void {
    this.expectedCargoQtyFocused = true;
    const parsed = this.toNumber(this.filters.expectedCargoQty);
    this.expectedCargoQtyDisplay =
      parsed === null ? '' : String(parsed);
  }

  onExpectedCargoQtyBlur(): void {
    this.expectedCargoQtyFocused = false;
    this.syncExpectedCargoQtyDisplay();
  }

  onExpectedCargoDaysChange(): void {
    // Keep total bunker in sync when days are manually adjusted (idle still uses idle time).
    if (!this.totalExpectedBunkerManual) {
      this.recalculateTotalExpectedBunker();
    }
  }

  onExpectedIdleTimeChange(): void {
    if (!this.totalExpectedBunkerManual) {
      this.recalculateTotalExpectedBunker();
    }
  }

  onTotalExpectedBunkerChange(value: number | string | null): void {
    const parsed = this.toNumber(value);
    this.totalExpectedBunkerConsumption = parsed;
    this.totalExpectedBunkerManual = parsed !== null;
  }

  private syncExpectedCargoQtyDisplay(): void {
    if (this.expectedCargoQtyFocused) {
      return;
    }
    const parsed = this.toNumber(this.filters.expectedCargoQty);
    this.expectedCargoQtyDisplay =
      parsed === null ? '' : this.formatCargoQuantity(parsed);
  }

  private parseFormattedNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const cleaned = String(value).replace(/,/g, '').trim();
    if (!cleaned) {
      return null;
    }
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Prefill Expected Cargo Days from API cargo_days_required after Analyse
   * (triggered by Expected Cargo Quantity submission).
   */
  private syncExpectedCargoDaysFromResponse(): void {
    const days =
      this.toNumber(this.expectedOptimizationResult?.['cargo_days_required']) ??
      this.toNumber(this.actualOptimizationResult?.['cargo_days_required']);
    if (days !== null) {
      this.filters.expectedCargoDays = days;
    }
  }

  /* ---------------- VESSEL & PORT SECTION HELPERS ---------------- */

  fetchVesselPortDetails(): void {
    const vesselNames = this.resolveSelectedVesselNames();
    const ports = this.resolveSelectedPortNames();
    const segments = this.resolveSelectedSegmentKeys();
    const operations = this.resolveSelectedOperationValues();
    // Invalidates any in-flight lookup so a stale response can't auto-run
    // optimization against filters that have since changed.
    const requestSeq = ++this.vesselPortRequestSeq;

    if (!vesselNames.length && !ports.length) {
      this.vesselPortVisits = [];
      this.selectedVesselPortVisit = null;
      this.selectedVisitIndex = 0;
      this.isLoadingVesselPort = false;
      return;
    }

    const vesselImos = vesselNames
      .map((name) => this.resolveVesselImo(name))
      .filter((imo): imo is string => Boolean(imo));

    this.isLoadingVesselPort = true;

    const payload = {
      vessel_names: vesselNames,
      vessel_imos: vesselImos,
      ports: ports,
      segments: segments,
      operations: operations,
    };

    this.service.getVesselPortOptimizationData(payload).subscribe({
      next: (res: any) => {
        if (requestSeq !== this.vesselPortRequestSeq) {
          return;
        }
        this.isLoadingVesselPort = false;
        if (res && res.status === 'success' && Array.isArray(res.visits) && res.visits.length > 0) {
          this.vesselPortVisits = res.visits;
          this.selectedVisitIndex = 0;
          this.selectedVesselPortVisit = res.visits[0];

          // Auto-sync missing inputs from the visit so user doesn't have to fill every field
          this.selectedOperationValues = (this.selectedOperationValues || []).filter((op: any) =>
            this.isValidOperation(op),
          );
          this.filters.operation = (this.filters.operation || []).filter((op: any) =>
            this.isValidOperation(op),
          );

          const firstValidVisit = (res.visits || []).find((v: any) =>
            this.isValidOperation(v?.operations),
          );
          if (
            !this.selectedOperationValues?.length &&
            !this.filters.operation?.length &&
            firstValidVisit?.operations
          ) {
            this.selectedOperationValues = [firstValidVisit.operations];
            this.filters.operation = [firstValidVisit.operations];
            this.onOperationChange();
          }
          const primaryVisit = firstValidVisit || res.visits[0];
          if (!this.selectedSegmentKeys?.length && !this.filters.segment?.length && primaryVisit?.segment) {
            const segItem = (this.segments || []).find((s: any) =>
              (s.value || s.key || '').toLowerCase() === primaryVisit.segment.toLowerCase()
            );
            const segKey = segItem ? segItem.key : primaryVisit.segment;
            this.selectedSegmentKeys = [segKey];
            this.filters.segment = [primaryVisit.segment];
          }
          if (!this.selectedOilTypes?.length && !this.filters.oil?.length) {
            this.selectedOilTypes = ['Crude Oil'];
            this.filters.oil = ['Crude Oil'];
          }
          if (this.toNumber(this.filters.expectedIdleTimeDays) === null) {
            this.filters.expectedIdleTimeDays = '1';
          }
          if (this.toNumber(this.filters.expectedCargoQty) === null && primaryVisit?.cargo_quentity) {
            this.filters.expectedCargoQty = String(primaryVisit.cargo_quentity);
            this.syncExpectedCargoQtyDisplay();
          }

          this.runAutoOptimization();
        } else {
          this.vesselPortVisits = [];
          this.selectedVesselPortVisit = null;
          this.selectedVisitIndex = 0;
        }
      },
      error: (err: any) => {
        if (requestSeq !== this.vesselPortRequestSeq) {
          return;
        }
        this.isLoadingVesselPort = false;
        this.vesselPortVisits = [];
        this.selectedVesselPortVisit = null;
        this.selectedVisitIndex = 0;
        console.error('Error fetching vessel port details:', err);
      },
    });
  }

  private runAutoOptimization(): void {
    const requestSeq = ++this.analyzeRequestSeq;
    this.isLoadingActual = true;
    this.isLoadingExpected = true;
    this.runPortOptimizationRequest(requestSeq);
  }

  selectVesselPortVisit(index: number): void {
    if (this.vesselPortVisits && this.vesselPortVisits[index]) {
      this.selectedVisitIndex = index;
      this.selectedVesselPortVisit = this.vesselPortVisits[index];
      const visit = this.vesselPortVisits[index];
      if (
        visit.operations &&
        this.isValidOperation(visit.operations) &&
        this.filters.operation?.[0] !== visit.operations
      ) {
        this.selectedOperationValues = [visit.operations];
        this.filters.operation = [visit.operations];
        this.onOperationChange();
      }
      if (visit.cargo_quentity) {
        this.filters.expectedCargoQty = String(visit.cargo_quentity);
        this.syncExpectedCargoQtyDisplay();
      }
      this.runAutoOptimization();
    }
  }

  formatVesselPortDate(dateValue: any): string {
    if (!dateValue) {
      return '--';
    }
    const formatted = formatPcUtcDateTime(dateValue);
    return formatted && formatted !== '-' ? formatted : '--';
  }

  formatVesselPortMetric(value: any, decimals = 2): string {
    const num = this.toNumber(value);
    if (num === null || num === undefined) {
      return '--';
    }
    return this.formatMetric(num, decimals);
  }

  hasVesselPortSelection(): boolean {
    const vesselNames = this.resolveSelectedVesselNames();
    const ports = this.resolveSelectedPortNames();
    return vesselNames.length > 0 || ports.length > 0;
  }

  get filteredVesselPortVisits(): any[] {
    if (!this.vesselPortVisits) return [];
    if (!this.optVoyageSearchTerm.trim()) return this.vesselPortVisits;
    const term = this.optVoyageSearchTerm.trim().toLowerCase();
    return this.vesselPortVisits.filter((v: any) =>
      String(v.voyage_number || '').toLowerCase().includes(term) ||
      String(v.port || '').toLowerCase().includes(term) ||
      String(v.operations || '').toLowerCase().includes(term) ||
      String(v.segment || '').toLowerCase().includes(term) ||
      String(v.vessel_name || '').toLowerCase().includes(term)
    );
  }

  /**
   * Total Expected Bunker = cargo ops bunker + idle total + manoeuvring total.
   */
  private recalculateTotalExpectedBunker(): void {
    const cargoOps =
      this.toNumber(
        this.expectedOptimizationResult?.['expected_total_consumption'],
      ) ?? 0;
    const manoeuvring =
      this.toNumber(
        this.expectedOptimizationResult?.['manoevering_consumption_per_day'],
      ) ?? 0;
    const idlePerDay =
      this.toNumber(
        this.expectedOptimizationResult?.['idle_consumption_per_day'],
      ) ?? 0;
    const idleDays =
      this.toNumber(this.filters.expectedIdleTimeDays) ??
      this.lastSubmittedExpectedIdleTimeDays ??
      0;
    const idleTotal = idlePerDay * idleDays;
    const sum = cargoOps + manoeuvring + idleTotal;
    this.totalExpectedBunkerConsumption = Number.isFinite(sum)
      ? Number(sum.toFixed(4))
      : null;
    this.totalExpectedBunkerManual = false;
  }

  private getBaselineOperationFieldKey(): string {
    if (this.isLoadingOperation()) {
      return 'design_loading_consumption';
    }
    if (this.isDischargingOperation()) {
      return 'design_discharging_consumption';
    }
    return 'design_discharging_consumption';
  }

  formatBaselineOperationConsumption(): string {
    return this.formatBaselineField(this.getBaselineOperationFieldKey());
  }

  isValidOperation(op: any): boolean {
    if (!op || typeof op !== 'string') {
      return false;
    }
    const clean = op.trim().toLowerCase();
    if (clean === 'transit' || clean.includes('transit')) {
      return false;
    }
    if (this.operations && this.operations.length > 0) {
      return this.operations.some(
        (o: string) => String(o).trim().toLowerCase() === clean,
      );
    }
    return clean === 'loading' || clean === 'discharging';
  }

  private getSelectedOperation(): string {
    const raw = String(
      this.selectedOperationValues?.[0] ?? this.filters.operation?.[0] ?? '',
    ).trim();
    return this.isValidOperation(raw) ? raw : '';
  }

  private isLoadingOperation(): boolean {
    const operation = this.getSelectedOperation().toLowerCase();
    return operation.includes('load') && !operation.includes('discharg');
  }

  private isDischargingOperation(): boolean {
    const operation = this.getSelectedOperation().toLowerCase();
    return operation.includes('discharg') || operation.includes('discharge');
  }

  hasAnalysisResults(): boolean {
    return !!(
      this.actualOptimizationResult ||
      this.expectedOptimizationResult ||
      this.technicalBaselineResult
    );
  }

  private extractSectionSource(res: any, section: OptimizationSection): any {
    if (!res || typeof res !== 'object') {
      return null;
    }

    if (section === 'actual') {
      const actual = res['Actual Values'];
      return actual && typeof actual === 'object' ? actual : null;
    }

    if (section === 'expected') {
      const expected = res['Expected Values'];
      return expected && typeof expected === 'object' ? expected : null;
    }

    const technicalRoot = res['As Per Technical Baseline'];
    if (!technicalRoot || typeof technicalRoot !== 'object') {
      return null;
    }

    const nestedBaseline = technicalRoot?.expected_cargo_baseline;
    const merged: Record<string, unknown> = {
      ...technicalRoot,
      ...(nestedBaseline && typeof nestedBaseline === 'object'
        ? nestedBaseline
        : {}),
    };
    delete merged['expected_cargo_baseline'];
    return Object.keys(merged).length ? merged : null;
  }

  private unwrapPortOptimizationResponse(res: any): any {
    if (!res || typeof res !== 'object') {
      return res;
    }

    const nested = res.data ?? res.result ?? res.response;
    const candidate =
      nested && typeof nested === 'object' && !Array.isArray(nested)
        ? nested
        : res;

    return this.normalizePortOptimizationResponse(candidate);
  }

  /**
   * Prod returns nested sections; dev/legacy APIs may return a flat object.
   * Map flat fields into section objects without recalculating values.
   */
  private normalizePortOptimizationResponse(res: any): any {
    if (!res || typeof res !== 'object' || Array.isArray(res)) {
      return res;
    }

    if (
      res['Actual Values'] ||
      res['Expected Values'] ||
      res['As Per Technical Baseline']
    ) {
      return res;
    }

    const pickFields = (keys: string[]): Record<string, unknown> => {
      const section: Record<string, unknown> = {};
      keys.forEach((key) => {
        const value = res[key];
        if (value !== undefined && value !== null && value !== '') {
          section[key] = value;
        }
      });
      return section;
    };

    const actual = pickFields([
      'avg_cargo_qty',
      'avg_consumption_per_10000_mt',
      'avg_loading_discharging_rate',
      'cargo_days_required',
      'consumption_per_day',
      'expected_total_consumption',
      'idle_consumption_per_day',
      'manoevering_consumption_per_day',
      'manoevering_time_days',
    ]);

    const expected = pickFields([
      'avg_consumption_per_10000_mt',
      'avg_loading_discharging_rate',
      'cargo_days_required',
      'consumption_per_day',
      'expected_cargo_qty',
      'expected_total_consumption',
      'idle_consumption_per_day',
      'manoevering_consumption_per_day',
      'manoevering_time_days',
    ]);

    const baseline = pickFields([
      'design_discharging_consumption',
      'design_loading_consumption',
      'design_idle_consumption',
      'total_techbasline_consumption',
      'cargo_days_required',
      'consumption_per_day',
      'avg_loading_discharging_rate',
      'manoevering_consumption_per_day',
      'manoeuvring_consumption_per_day',
      'design_manoevering_consumption',
      'design_manoeuvring_consumption',
      'manoevering_time_days',
      'manoeuvring_time_days',
      'design_manoevering_time_days',
      'design_manoeuvring_time_days',
    ]);

    const normalized: Record<string, unknown> = {};

    if (Object.keys(actual).length) {
      normalized['Actual Values'] = actual;
    }
    if (Object.keys(expected).length) {
      normalized['Expected Values'] = expected;
    }
    if (Object.keys(baseline).length) {
      normalized['As Per Technical Baseline'] = {
        expected_cargo_baseline: baseline,
      };
    }

    return Object.keys(normalized).length ? normalized : res;
  }

  private applyPortOptimizationResponse(res: any): void {
    const payload = this.unwrapPortOptimizationResponse(res);
    this.actualOptimizationResult = this.extractSectionSource(payload, 'actual');
    this.expectedOptimizationResult = this.extractSectionSource(payload, 'expected');
    this.technicalBaselineResult = this.extractSectionSource(payload, 'baseline');
    this.syncExpectedCargoDaysFromResponse();
    this.recalculateTotalExpectedBunker();

    if (!this.filters.expectedCargoQty && this.expectedOptimizationResult?.['expected_cargo_qty']) {
      this.filters.expectedCargoQty = String(this.expectedOptimizationResult['expected_cargo_qty']);
      this.syncExpectedCargoQtyDisplay();
    }
  }

  private resetOptimizationResults(): void {
    this.actualOptimizationResult = null;
    this.expectedOptimizationResult = null;
    this.technicalBaselineResult = null;
    this.lastSubmittedExpectedIdleTimeDays = null;
    this.totalExpectedBunkerConsumption = null;
    this.totalExpectedBunkerManual = false;
  }

  private toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /* ---------------- DROPDOWN HANDLERS ---------------- */

  toggleSegmentDropdown(event: Event) {
    event.stopPropagation();
    this.segmentDropdownOpen = !this.segmentDropdownOpen;
    this.operationDropdownOpen = false;
    this.portDropdownOpen = false;
    this.oilDropdownOpen = false;
  }

  toggleOperationDropdown(event: Event) {
    event.stopPropagation();
    this.operationDropdownOpen = !this.operationDropdownOpen;
    this.segmentDropdownOpen = false;
    this.portDropdownOpen = false;
    this.oilDropdownOpen = false;
  }

  togglePortDropdown(event: Event) {
    event.stopPropagation();
    this.portDropdownOpen = !this.portDropdownOpen;
    this.segmentDropdownOpen = false;
    this.operationDropdownOpen = false;
    this.oilDropdownOpen = false;
  }

  toggleOilDropdown(event: Event) {
    event.stopPropagation();
    this.oilDropdownOpen = !this.oilDropdownOpen;
    this.segmentDropdownOpen = false;
    this.operationDropdownOpen = false;
    this.portDropdownOpen = false;
  }

  closeAllDropdowns() {
    this.segmentDropdownOpen = false;
    this.operationDropdownOpen = false;
    this.portDropdownOpen = false;
    this.oilDropdownOpen = false;
  }

  clearGlobalFilters() {
    this.filters = {
      segment: [],
      operation: [],
      port: [],
      cargoQty: '',
      expectedCargoQty: '',
      expectedCargoDays: '',
      expectedIdleTimeDays: '',
      oil: [],
    };
    this.expectedCargoQtyDisplay = '';
    this.totalExpectedBunkerConsumption = null;
    this.totalExpectedBunkerManual = false;
    this.operations = [];
    this.operationsCounts = {};
    this.ports = [];
    this.portsCounts = {};
    this.vesselNameOptions = [];
    this.selectedSegmentKeys = [];
    this.selectedOperationValues = [];
    this.selectedPortNames = [];
    this.selectedOilTypes = [];
    this.selectedVesselNames = [];
    this.vesselPortVisits = [];
    this.selectedVesselPortVisit = null;
    this.selectedVisitIndex = 0;
    this.isLoadingVesselPort = false;
    this.resetOptimizationResults();
    this.toastr.info('All filters cleared');
  }

  /* ---------------- FILTERING LOGIC ---------------- */

  get segmentFilterOptions(): string[] {
    return (this.segments || []).map((seg: any) => seg.key);
  }

  get operationFilterOptions(): string[] {
    return [...(this.operations || [])];
  }

  get operationFilterOptionItems(): PcMultiSelectOption[] {
    return (this.operations || []).map((operation: string) => ({
      value: operation,
      label: this.formatFilterOptionLabel(operation, this.operationsCounts),
    }));
  }

  get portFilterOptions(): string[] {
    return (this.ports || []).map((port: any) => port.name);
  }

  get portFilterOptionItems(): PcMultiSelectOption[] {
    return (this.ports || []).map((port: any) => ({
      value: port.name,
      label: this.formatFilterOptionLabel(port.name, this.portsCounts),
    }));
  }

  private formatFilterOptionLabel(
    name: string,
    counts: Record<string, number>,
  ): string {
    if (!name) {
      return '';
    }

    const count = counts?.[name];
    return count !== undefined && count !== null ? `${name} (${count})` : name;
  }

  get cargoTypeFilterOptions(): string[] {
    return [...(this.cargoTypes || [])];
  }

  onSegmentSelectionChange(selected: string[]) {
    const previousSegments = [...(this.selectedSegmentKeys || [])].sort().join('|');
    this.selectedSegmentKeys = selected || [];
    this.filters.segment = (selected || []).flatMap((key: string) => {
      const seg = (this.segments || []).find((item: any) => item.key === key);
      return seg?.value || key;
    });

    const nextSegments = [...(this.selectedSegmentKeys || [])].sort().join('|');
    if (previousSegments !== nextSegments) {
      this.selectedPortNames = [];
      this.filters.port = [];
    }

    this.onSegmentChange();
    this.fetchVesselPortDetails();
  }

  onOperationSelectionChange(selected: string[]) {
    const validSelected = (selected || []).filter((op: any) => this.isValidOperation(op));
    const previousOperation = this.selectedOperationValues?.[0] ?? '';
    this.selectedOperationValues = validSelected;
    this.filters.operation = [...this.selectedOperationValues];

    const nextOperation = this.selectedOperationValues?.[0] ?? '';
    if (previousOperation !== nextOperation) {
      this.selectedPortNames = [];
      this.filters.port = [];
    }

    this.onOperationChange();
    this.fetchVesselPortDetails();
  }

  onPortSelectionChange(selected: string[]) {
    this.selectedPortNames = selected || [];
    this.reconcilePortSelection();
    this.fetchVesselPortDetails();
  }

  private syncFiltersFromUiSelections(): void {
    this.filters.segment = (this.selectedSegmentKeys || []).flatMap((key: string) => {
      const seg = (this.segments || []).find((item: any) => item.key === key);
      return seg?.value || key;
    });
    this.filters.operation = [...(this.selectedOperationValues || [])];
    this.filters.oil = [...(this.selectedOilTypes || [])];
    this.reconcilePortSelection();
  }

  private reconcilePortSelection(): void {
    const uniqueSelection = [
      ...new Set(
        (this.selectedPortNames || [])
          .map((port) => String(port).trim())
          .filter(Boolean),
      ),
    ];
    const availablePorts = this.ports || [];

    if (!uniqueSelection.length) {
      this.filters.port = [];
      this.selectedPortNames = [];
      return;
    }

    if (!availablePorts.length) {
      this.filters.port = uniqueSelection;
      this.selectedPortNames = [...uniqueSelection];
      return;
    }

    const reconciled = uniqueSelection
      .map((name) => {
        const match = availablePorts.find(
          (port) =>
            String(port.name).toLowerCase() === name.toLowerCase() ||
            String(port.value).toLowerCase() === name.toLowerCase(),
        );
        return match?.name ?? null;
      })
      .filter((name): name is string => Boolean(name));

    this.filters.port = [...new Set(reconciled)];
    this.selectedPortNames = [...this.filters.port];
  }

  onOilSelectionChange(selected: string[]) {
    this.selectedOilTypes = selected || [];
    this.filters.oil = [...this.selectedOilTypes];
  }

  getFilteredSegments() {
    if (!this.segments) return [];
    return this.segments.filter(seg => 
      seg?.key?.toLowerCase().includes(this.segmentSearchQuery.toLowerCase())
    );
  }

  getFilteredOperations() {
    if (!this.operations) return [];
    return this.operations.filter(op => 
      op?.toLowerCase().includes(this.operationSearchQuery.toLowerCase())
    );
  }

  getFilteredPortsList() {
    if (!this.ports) return [];
    return this.ports.filter(port => 
      port?.name?.toLowerCase().includes(this.portSearchQuery.toLowerCase())
    );
  }

  getFilteredOilTypes() {
    if (!this.cargoTypes) return [];
    return this.cargoTypes.filter(oil => 
      oil?.toLowerCase().includes(this.oilSearchQuery.toLowerCase())
    );
  }

  /* ---------------- SELECTION HANDLERS (MULTI-SELECT) ---------------- */

  toggleSelection(type: string, value: any) {
    const array = [...(this.filters[type] || [])];
    const index = array.indexOf(value);
    if (index > -1) {
      array.splice(index, 1);
    } else {
      array.push(value);
    }
    this.filters[type] = array;
  }

  isItemSelected(array: any[], value: any): boolean {
    if (!array) return false;
    return array.includes(value);
  }

  /* --- SELECT ALL / CLEAR ALL --- */

  selectAll(type: string) {
    if (type === 'segment') {
      this.filters.segment = this.segments.map(s => s.value);
      this.onSegmentChange();
    } else if (type === 'operation') {
      this.filters.operation = [...this.operations];
      this.onOperationChange();
    } else if (type === 'port') {
      this.filters.port = this.ports.map(p => p.name);
      this.selectedPortNames = [...this.filters.port];
    } else if (type === 'oil') {
      this.filters.oil = [...this.cargoTypes];
    }
  }

  clearAll(type: string) {
    this.filters[type] = [];
    if (type === 'segment') {
      this.onSegmentChange();
    } else if (type === 'operation') {
      this.onOperationChange();
    }
  }

  isAllSelected(type: string): boolean {
    const selected = this.filters[type];
    if (!selected || selected.length === 0) return false;
    
    if (type === 'segment') return selected.length === (this.segments?.length || 0);
    if (type === 'operation') return selected.length === (this.operations?.length || 0);
    if (type === 'port') return selected.length === (this.ports?.length || 0);
    if (type === 'oil') return selected.length === (this.cargoTypes?.length || 0);
    
    return false;
  }

  selectSegment(seg: any) {
    this.toggleSelection('segment', seg.value);
    this.onSegmentChange();
  }

  selectOperation(op: any) {
    this.toggleSelection('operation', op);
    this.onOperationChange();
  }

  selectPortItem(port: any) {
    this.toggleSelection('port', port.name);
  }

  selectOilType(oil: any) {
    this.toggleSelection('oil', oil);
  }

  getSelectionDisplay(type: string): string {
    const selected = this.filters[type];
    if (!selected || selected.length === 0) return `Select ${type.charAt(0).toUpperCase() + type.slice(1)}`;
    if (selected.length === 1) {
      if (type === 'segment') {
        const seg = this.segments.find(s => s.value === selected[0]);
        return seg ? seg.key : selected[0];
      }
      return selected[0];
    }
    if (this.isAllSelected(type)) return `All ${type.charAt(0).toUpperCase() + type.slice(1)}s`;
    return `${selected.length} Selected`;
  }

  getRateCardTitle(): string {
    if (this.isLoadingOperation()) {
      return 'Average Loading Rate';
    }
    if (this.isDischargingOperation()) {
      return 'Average Discharging Rate';
    }
    return 'Average Loading/Discharging Rate';
  }

  /* ---------------- API CALLS ---------------- */

  getSegments() {
    this.isSegmentLoading = true;
    this.service.getSegmentsName().subscribe(
      (res: any) => {
        console.log('Segments loaded:', res);
        this.segments = res.segments || [];
        
   if (this.subusername?.toLowerCase() === 'demo') {
        const defaultSegment = this.segments.find(
          (seg: any) =>
            String(seg.key).trim().toLowerCase() === 'lr3/suezmax'
        );

        if (defaultSegment) {
          this.selectedSegmentKeys = [defaultSegment.key];
          this.filters.segment = [defaultSegment.value];

          // Load dependent filters for the default segment
          this.onSegmentChange();
        }
      } else {
        this.selectedSegmentKeys = this.selectedSegmentKeys.filter((key) =>
          this.segmentFilterOptions.includes(key)
        );
      }
        this.isSegmentLoading = false;
      },
      (err) => {
        console.error('Error fetching segments:', err);
        this.isSegmentLoading = false;
      }
    );
  }

  onSegmentChange() {
    const requestSeq = ++this.segmentCascadeSeq;

    // Clear child lists
    this.operations = [];
    this.operationsCounts = {};
    this.ports = [];
    this.portsCounts = {};
    this.vesselNameOptions = [];
    
    if (!this.filters.segment || this.filters.segment.length === 0) {
      this.filters.operation = [];
      this.filters.port = [];
      this.selectedOperationValues = [];
      this.selectedPortNames = [];
      this.selectedVesselNames = [];
      return;
    }

    this.isOperationLoading = true;
    
    const segments = this.normalizeSegmentValues(this.filters.segment);
    const jsonData = {
      segment: segments,
    };

    this.loadVesselNamesForSegments(segments, requestSeq);

    this.service.getOptimizationOperations(jsonData).subscribe(
      (res: any) => {
        if (requestSeq !== this.segmentCascadeSeq) {
          return;
        }
        this.operations = (res.operations || []).filter(
          (op: string) => !String(op).trim().toLowerCase().includes('transit'),
        );
        this.operationsCounts = res.operations_counts || {};
        this.isOperationLoading = false;
        this.selectedOperationValues = (this.selectedOperationValues || []).filter((op: any) =>
          this.isValidOperation(op),
        );
        
        if (this.filters.operation && this.filters.operation.length > 0) {
          this.filters.operation = this.filters.operation.filter((op: any) =>
            this.isValidOperation(op),
          );
          this.selectedOperationValues = [...this.filters.operation];
        }
        
        this.onOperationChange(requestSeq);
      },
      () => {
        if (requestSeq !== this.segmentCascadeSeq) {
          return;
        }
        this.isOperationLoading = false;
        this.operations = [];
        this.operationsCounts = {};
      }
    );
  }

  onOperationChange(requestSeq?: number) {
    this.ports = [];
    this.portsCounts = {};

    if (
      !this.filters.operation ||
      this.filters.operation.length === 0 ||
      !this.filters.segment ||
      this.filters.segment.length === 0
    ) {
      this.filters.port = [];
      this.selectedPortNames = [];
      return;
    }

    this.isPortLoading = true;
    
    const segments = this.normalizeSegmentValues(this.filters.segment);
    
    const jsonData = {
      segment: segments,
      operation: this.filters.operation[0] // Passing the first selected operation as a string
    };

    this.service.getOptimizationNamePorts(jsonData).subscribe(
      (res: any) => {
        if (requestSeq != null && requestSeq !== this.segmentCascadeSeq) {
          return;
        }
        const portData = res.ports || {};
        this.portsCounts = res.ports_counts || {};
        
        if (Array.isArray(portData)) {
          this.ports = portData.map(p => ({
            name: typeof p === 'string' ? p : (p.name || p.port_name || p),
            value: typeof p === 'string' ? p : (p.value || p.name || p)
          }));
        } else {
          this.ports = Object.keys(portData).map(key => ({
            name: key,
            value: key
          }));
        }
        
        this.isPortLoading = false;
        this.reconcilePortSelection();
      },
      () => {
        if (requestSeq != null && requestSeq !== this.segmentCascadeSeq) {
          return;
        }
        this.isPortLoading = false;
        this.ports = [];
        this.portsCounts = {};
      }
    );
  }

  /* ---------------- MAIN API ---------------- */

  private resolveSegmentPayload(): string[] {
    const segmentSource = (this.selectedSegmentKeys || []).length
      ? (this.selectedSegmentKeys || []).flatMap((key: string) => {
          const seg = (this.segments || []).find((item: any) => item.key === key);
          return seg?.value || key;
        })
      : Array.isArray(this.filters.segment)
        ? this.filters.segment
        : [this.filters.segment];

    return this.normalizeSegmentValues(segmentSource);
  }

  private buildOptimizationFilterPayload(): {
    segment: string[];
    operation: string;
    port_name: string[];
    oil: string;
    vessel_name?: string[];
    vessel_imo?: string[];
  } {
    const payload: {
      segment: string[];
      operation: string;
      port_name: string[];
      oil: string;
      vessel_name?: string[];
      vessel_imo?: string[];
    } = {
      segment: this.resolveSegmentPayload(),
      operation: String(
        this.selectedOperationValues?.[0] ?? this.filters.operation?.[0] ?? '',
      ).trim(),
      port_name: [
        ...new Set(
          (this.selectedPortNames || [])
            .map((port) => String(port).trim())
            .filter(Boolean),
        ),
      ],
      oil: String(
        this.selectedOilTypes?.[0] ?? this.filters.oil?.[0] ?? '',
      ).trim(),
    };

    const vesselNames = this.resolveSelectedVesselNames();
    if (vesselNames.length) {
      payload.vessel_name = vesselNames;
    }

    return payload;
  }

  private buildPortOptimizationPayload(): Record<string, unknown> | null {
    const filterPayload = this.buildOptimizationFilterPayload();
    let expectedIdleTimeDays = this.toNumber(
      this.filters.expectedIdleTimeDays,
    );
    if (expectedIdleTimeDays === null) {
      expectedIdleTimeDays = 1;
    }

    let expectedCargoQty = this.toNumber(this.filters.expectedCargoQty);
    if (expectedCargoQty === null) {
      expectedCargoQty =
        this.toNumber(this.actualOptimizationResult?.['avg_cargo_qty']) ||
        this.toNumber(this.selectedVesselPortVisit?.cargo_quentity) ||
        this.toNumber(this.vesselPortVisits?.[0]?.cargo_quentity) ||
        null;
    }
    const expectedCargoDays = this.toNumber(this.filters.expectedCargoDays);

    const vesselNames = filterPayload.vessel_name ?? [];
    if (vesselNames.length) {
      const vesselImos = vesselNames
        .map((name) => this.resolveVesselImo(name))
        .filter(Boolean);
      if (vesselImos.length) {
        filterPayload.vessel_imo = vesselImos;
      }
    }

    const segments = filterPayload.segment?.length
      ? filterPayload.segment
      : (this.selectedVesselPortVisit?.segment
          ? [this.selectedVesselPortVisit.segment]
          : (this.vesselPortVisits?.[0]?.segment ? [this.vesselPortVisits[0].segment] : []));

    const rawFilterOp = Array.isArray(filterPayload.operation)
      ? filterPayload.operation[0]
      : filterPayload.operation;
    const visitWithValidOp = this.vesselPortVisits?.find((v: any) =>
      this.isValidOperation(v?.operations),
    );
    const validOp = this.isValidOperation(rawFilterOp)
      ? rawFilterOp
      : (this.isValidOperation(this.selectedVesselPortVisit?.operations)
          ? this.selectedVesselPortVisit?.operations
          : null) ||
        visitWithValidOp?.operations ||
        this.operations?.[0] ||
        'Loading';

    const operation = [validOp];

    // With no port selected, borrow a visit's port only if that visit had the
    // same operation; otherwise e.g. a Loading-only port gets queried for
    // Discharging and the API 400s. An empty list lets the API match by cargo range.
    const sameOp = (visit: any) =>
      Boolean(visit?.port) &&
      String(visit?.operations || '').trim().toLowerCase() ===
        String(validOp || '').trim().toLowerCase();
    const fallbackVisit = sameOp(this.selectedVesselPortVisit)
      ? this.selectedVesselPortVisit
      : this.vesselPortVisits?.find(sameOp);
    const ports = filterPayload.port_name?.length
      ? filterPayload.port_name
      : (fallbackVisit ? [fallbackVisit.port] : []);

    return {
      segment: segments,
      operation: operation,
      port_name: ports,
      oil: filterPayload.oil || 'Crude Oil',
      ...(filterPayload.vessel_name?.length
        ? {
            vessel_name: filterPayload.vessel_name,
            ...(filterPayload.vessel_imo?.length
              ? { vessel_imo: filterPayload.vessel_imo }
              : {}),
          }
        : {}),
      expected_idle_time_days: expectedIdleTimeDays,
      expected_cargo_qty: expectedCargoQty,
      ...(expectedCargoDays !== null
        ? { expected_cargo_days: expectedCargoDays }
        : {}),
    };
  }

  private runPortOptimizationRequest(requestSeq: number): void {
    const payload = this.buildPortOptimizationPayload();
    if (!payload) {
      this.isLoading = false;
      this.isLoadingActual = false;
      this.isLoadingExpected = false;
      return;
    }

    this.isLoadingActual = true;
    this.isLoadingExpected = true;

    this.service.getOptimization(payload).subscribe({
      next: (res) => {
        if (requestSeq !== this.analyzeRequestSeq) {
          return;
        }
        this.applyPortOptimizationResponse(res);
        this.lastSubmittedExpectedIdleTimeDays = this.toNumber(
          payload['expected_idle_time_days'],
        );
        this.isLoading = false;
        this.isLoadingActual = false;
        this.isLoadingExpected = false;
      },
      error: (error) => {
        if (requestSeq !== this.analyzeRequestSeq) {
          return;
        }
        this.isLoading = false;
        this.isLoadingActual = false;
        this.isLoadingExpected = false;
        this.resetOptimizationResults();
        this.toastr.error(this.extractErrorMessage(error), 'Optimization');
      },
    });
  }

  private extractErrorMessage(error: any): string {
    return (
      error?.error?.message ||
      error?.error?.error ||
      (typeof error?.error === 'string' ? error.error : null) ||
      'Calculation failed'
    );
  }

  calculateOptimization() {
    if (this.isLoading) {
      return;
    }

    this.syncFiltersFromUiSelections();

    // Auto-fill segment if missing but vessel or visit is selected
    if (!this.selectedSegmentKeys?.length && !this.filters.segment?.length) {
      const visitSegment = this.selectedVesselPortVisit?.segment || this.vesselPortVisits?.[0]?.segment;
      if (visitSegment) {
        const segItem = (this.segments || []).find((s: any) =>
          (s.value || s.key || '').toLowerCase() === visitSegment.toLowerCase()
        );
        const segKey = segItem ? segItem.key : visitSegment;
        this.selectedSegmentKeys = [segKey];
        this.filters.segment = [visitSegment];
      }
    }

    // Auto-fill operation if missing
    this.selectedOperationValues = (this.selectedOperationValues || []).filter((op: any) =>
      this.isValidOperation(op),
    );
    this.filters.operation = (this.filters.operation || []).filter((op: any) =>
      this.isValidOperation(op),
    );

    if (!this.selectedOperationValues?.length && !this.filters.operation?.length) {
      const visitWithValidOp = this.vesselPortVisits?.find((v: any) =>
        this.isValidOperation(v?.operations),
      );
      const visitOp =
        (this.isValidOperation(this.selectedVesselPortVisit?.operations)
          ? this.selectedVesselPortVisit?.operations
          : null) ||
        visitWithValidOp?.operations ||
        this.operations?.[0] ||
        'Loading';
      this.selectedOperationValues = [visitOp];
      this.filters.operation = [visitOp];
      this.onOperationChange();
    }

    // Auto-fill oil if missing
    if (!this.selectedOilTypes?.length && !this.filters.oil?.length) {
      this.selectedOilTypes = ['Crude Oil'];
      this.filters.oil = ['Crude Oil'];
    }

    // Auto-fill idle days if missing
    if (this.toNumber(this.filters.expectedIdleTimeDays) === null) {
      this.filters.expectedIdleTimeDays = '1';
    }

    // Auto-fill expected cargo qty if missing
    if (this.toNumber(this.filters.expectedCargoQty) === null) {
      const visitCargo = this.selectedVesselPortVisit?.cargo_quentity || this.vesselPortVisits?.[0]?.cargo_quentity;
      if (visitCargo) {
        this.filters.expectedCargoQty = String(visitCargo);
        this.syncExpectedCargoQtyDisplay();
      }
    }

    const hasVessel = (this.selectedVesselNames?.length || 0) > 0;
    const hasPort = (this.selectedPortNames?.length || 0) > 0 || (this.filters.port?.length || 0) > 0;
    const hasSegment = (this.selectedSegmentKeys?.length || 0) > 0 || (this.filters.segment?.length || 0) > 0;

    if (!hasSegment && !hasVessel) {
      this.toastr.warning('Please select at least one segment or vessel.', 'Missing filters');
      return;
    }

    if (!hasPort && !hasVessel) {
      this.toastr.warning('Please select at least one port.', 'Missing filters');
      return;
    }

    this.isLoading = true;
    this.isLoadingActual = true;
    this.isLoadingExpected = true;
    this.resetOptimizationResults();

    const requestSeq = ++this.analyzeRequestSeq;

    this.refreshVesselImoForAnalyze().subscribe({
      next: () => {
        if (requestSeq !== this.analyzeRequestSeq) {
          return;
        }
        this.runPortOptimizationRequest(requestSeq);
      },
      error: () => {
        if (requestSeq !== this.analyzeRequestSeq) {
          return;
        }
        this.runPortOptimizationRequest(requestSeq);
      },
    });
  }

  takeScreenshot() {
    // Popup (Create Voyage View Row Yes/No) commented out — screenshot only, copied to clipboard for Ctrl+V / Cmd+V.
    // const dialogRef = this.dialog.open(ConfirmPopupComponent, {
    //   width: '450px',
    //   disableClose: true,
    //   panelClass: 'custom-dialog-container',
    //   data: {
    //     mainTitle: 'Create Voyage View Row?',
    //     title:
    //       'Yes: capture screenshot and create a Voyage View row with Optimization Consumption values.<br/>No: capture screenshot only.',
    //     yesText: 'Yes',
    //     noText: 'No',
    //     iconClass: 'fa-solid fa-camera',
    //   },
    // });
    //
    // dialogRef.afterClosed().subscribe((createVoyageRow: boolean | undefined) => {
    //   if (createVoyageRow === undefined) {
    //     return;
    //   }
    //   this.captureScreenshot(!!createVoyageRow);
    // });

    this.captureScreenshot(false);
  }

  private captureScreenshot(createVoyageRow: boolean): void {
    const element = document.querySelector('.opt-page') as HTMLElement;
    const actions = document.querySelector(
      '.opt-header__actions',
    ) as HTMLElement | null;

    if (!element) {
      this.toastr.warning('Could not find content to capture');
      return;
    }

    if (actions) {
      actions.style.visibility = 'hidden';
    }

    const flash = document.createElement('div');
    flash.className = 'screenshot-flash';
    document.body.appendChild(flash);
    setTimeout(() => {
      if (document.body.contains(flash)) {
        document.body.removeChild(flash);
      }
    }, 300);

    html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: this.isDarkMode() ? '#08111f' : '#f8fafc',
      logging: false,
      ignoreElements: (el) => {
        return el.classList?.contains('opt-header__actions') || false;
      },
      onclone: (clonedDoc, clonedElement) => {
        const view = clonedDoc.defaultView || window;

        // 1. Swap input values with visible text elements for screenshot rendering
        const inputs = clonedElement.querySelectorAll('.opt-hero-editable-input');
        const staticValues = clonedElement.querySelectorAll('.opt-hero-screenshot-value');
        inputs.forEach((input) => {
          (input as HTMLElement).style.display = 'none';
        });
        staticValues.forEach((span) => {
          (span as HTMLElement).style.display = 'inline-block';
        });

        // 2. Ensure vertical section rail labels render correctly in html2canvas
        // html2canvas does not support CSS writing-mode: vertical-rl well, resulting in stacked or clipped text.
        // We convert to standard horizontal writing-mode and rotate -90deg so it paints perfectly.
        const railLabels = clonedElement.querySelectorAll('.opt-section__name');
        railLabels.forEach((label) => {
          const lblEl = label as HTMLElement;
          lblEl.style.setProperty('writing-mode', 'horizontal-tb', 'important');
          lblEl.style.setProperty('transform', 'rotate(-90deg)', 'important');
          lblEl.style.setProperty('transform-origin', 'center center', 'important');
          lblEl.style.setProperty('white-space', 'nowrap', 'important');
          lblEl.style.setProperty('display', 'inline-block', 'important');
          lblEl.style.setProperty('width', 'max-content', 'important');
          lblEl.style.setProperty('margin-top', '60px', 'important');
        });

        // 3. Strip gradients and backgrounds from zero-dimension elements in the cloned document
        clonedElement.querySelectorAll('*').forEach((el) => {
          const htmlEl = el as HTMLElement;
          const rect = htmlEl.getBoundingClientRect();
          if (rect.width === 0 || rect.height === 0 || htmlEl.offsetWidth === 0 || htmlEl.offsetHeight === 0) {
            try {
              const style = view.getComputedStyle(htmlEl);
              if (style.backgroundImage && style.backgroundImage !== 'none') {
                htmlEl.style.setProperty('background-image', 'none', 'important');
                htmlEl.style.setProperty('background', 'transparent', 'important');
              }
            } catch (_) {}
          }
        });
      },
    })
      .then(async (canvas) => {
        if (actions) {
          actions.style.visibility = 'visible';
        }
        // setTimeout(() => {
        //   this.screenshotPreview = null;
        // }, 4000);
        //
        // const link = document.createElement('a');
        // link.download = `Optimization_Analytics_${new Date().getTime()}.png`;
        // link.href = imgData;
        // link.click();

        try {
          const blob = await new Promise<Blob | null>((resolve) => {
            canvas.toBlob(resolve, 'image/png');
          });

          if (!blob) {
            throw new Error('Failed to create screenshot image');
          }

          if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
            await navigator.clipboard.write([
              new ClipboardItem({ 'image/png': blob }),
            ]);
            this.toastr.success(
              'Screenshot copied. Paste it with Ctrl+V / Cmd+V.',
            );
          } else {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `Optimization_Analytics_${new Date().getTime()}.png`;
            link.href = url;
            link.click();
            URL.revokeObjectURL(url);
            this.toastr.info(
              'Clipboard not available. Screenshot downloaded instead.',
            );
          }
        } catch (copyErr) {
          console.error('Screenshot clipboard error:', copyErr);
          this.toastr.error(
            'Could not copy screenshot. Your browser may block clipboard access.',
          );
        }

        // if (createVoyageRow) {
        //   this.createVoyageViewRowFromOptimization();
        // }
      })
      .catch((err) => {
        if (actions) {
          actions.style.visibility = 'visible';
        }
        console.error('Screenshot error:', err);
        this.toastr.error('Failed to capture screenshot');
      });
  }

  /**
   * Builds bunker payload from the latest Analyse results and creates a
   * Voyage View row for the selected vessel. Does not change report generation.
   */
  private createVoyageViewRowFromOptimization(): void {
    const vesselNames = this.resolveSelectedVesselNames();
    if (!vesselNames.length) {
      this.toastr.warning(
        'Select a vessel before creating a Voyage View row.',
        'Voyage View',
      );
      return;
    }

    if (
      !this.expectedOptimizationResult &&
      !this.technicalBaselineResult &&
      !this.actualOptimizationResult
    ) {
      this.toastr.warning(
        'Run Analyse first so Optimization Consumption values can be populated.',
        'Voyage View',
      );
      return;
    }

    const bunker = this.buildOptimizationBunkerPayload();
    const vesselName = vesselNames[0];
    const vesselImo = this.lookupVesselImo(vesselName) || '';
    const ports = this.resolveSelectedPortNames();
    const operations = this.resolveSelectedOperationValues();
    const segments = this.resolveSelectedSegmentKeys();

    const payload: Record<string, unknown> = {
      vessel_name: vesselName,
      vessel_imo: vesselImo,
      port: ports[0] || '',
      operations: operations[0] || '',
      segment: segments[0] || '',
      oil: this.selectedOilTypes?.[0] || '',
      bunker,
    };

    this.service.createVesselViewOptimizationRow(payload).subscribe({
      next: () => {
        this.toastr.success(
          'Voyage View row created with Optimization Consumption values.',
          'Voyage View',
        );
      },
      error: (error) => {
        this.toastr.error(
          this.extractErrorMessage(error) ||
            'Failed to create Voyage View row.',
          'Voyage View',
        );
      },
    });
  }

  /**
   * Maps Analyse KPI fields into the bunker shape expected by Voyage View.
   * optimization_* ← Expected / Technical Baseline design targets.
   * man / cargo_ops / idle ← left empty here (filled after actuals/report).
   */
  private buildOptimizationBunkerPayload(): Record<string, number> {
    const expected = this.expectedOptimizationResult || {};
    const baseline = this.technicalBaselineResult || {};
    const idleDays =
      this.lastSubmittedExpectedIdleTimeDays ??
      this.toNumber(this.filters.expectedIdleTimeDays) ??
      0;

    const optimizationMan =
      this.toNumber(expected['manoevering_consumption_per_day']) ?? 0;

    const optimizationCargoOps =
      this.toNumber(expected['expected_total_consumption']) ??
      this.toNumber(baseline[this.getBaselineOperationFieldKey()]) ??
      0;

    const idlePerDay =
      this.toNumber(expected['idle_consumption_per_day']) ??
      this.toNumber(baseline['design_idle_consumption']);
    const optimizationIdle =
      idlePerDay != null && idleDays > 0
        ? idlePerDay * idleDays
        : idlePerDay ?? 0;

    const hsfo = 0;
    const vlsfo = 0;
    const go = 0;

    return {
      hsfo,
      vlsfo,
      go,
      man: 0,
      cargo_ops: 0,
      idle: 0,
      optimization_man: optimizationMan,
      optimization_cargo_ops: optimizationCargoOps,
      optimization_idle: optimizationIdle,
      agg_fueloil: hsfo + vlsfo,
    };
  }

  private resolveSelectedPortNames(): string[] {
    return (this.selectedPortNames || [])
      .map((name) => String(name).trim())
      .filter(Boolean);
  }

  private resolveSelectedOperationValues(): string[] {
    return (this.selectedOperationValues || [])
      .map((value) => String(value).trim())
      .filter((value) => this.isValidOperation(value));
  }

  private resolveSelectedSegmentKeys(): string[] {
    return (this.selectedSegmentKeys || [])
      .map((key) => String(key).trim())
      .filter(Boolean);
  }
}